import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "universal-cookie";
import { FaSearch, FaPlus, FaEdit, FaLock, FaTimes, FaSave, FaChevronRight, FaChevronDown, FaTrash } from "react-icons/fa";
import "../../css/TelefonosNotificacion.css";
import "../../css/Usuarios.css";
import "../../css/Perfiles.css";

const agruparModulosPlano = (listaPlana) => {
  const mapa = new Map();

  (listaPlana || []).forEach((item) => {
    if (!mapa.has(item.id_modulo)) {
      mapa.set(item.id_modulo, {
        id_modulo: item.id_modulo,
        nombre: item.modulo_nombre,
        orden: item.modulo_orden,
        submodulos: [],
      });
    }

    if (item.id_submodulo !== null) {
      const padre = mapa.get(item.id_modulo);
      if (padre && !padre.submodulos.some((s) => s.id_submodulo === item.id_submodulo)) {
        padre.submodulos.push({
          id_modulo: item.id_modulo,
          id_submodulo: item.id_submodulo,
          nombre: item.submodulo_nombre,
          orden: item.submodulo_orden,
        });
      }
    }
  });

  const arbol = Array.from(mapa.values());
  arbol.sort((a, b) => (a.orden ?? 99) - (b.orden ?? 99));
  arbol.forEach((mod) => {
    mod.submodulos.sort((a, b) => (a.orden ?? 99) - (b.orden ?? 99));
  });
  return arbol;
};

const Perfiles = () => {
  const cookies = new Cookies();
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // ── Tabla de perfiles ─────────────────────────────────────
  const [datos, setDatos] = useState([]);
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(false);

  // ── Modal crear / editar ──────────────────────────────────
  const [modalVisible, setModalVisible] = useState(false);
  const [editando, setEditando] = useState(false);
  const [perfilActivo, setPerfilActivo] = useState(null);
  const [nombrePerfil, setNombrePerfil] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [eliminando, setEliminando] = useState(null);

  // ── Sección de permisos inline ────────────────────────────
  const [permisosPerfil, setPermisosPerfil] = useState(null);
  const [modulosArbol, setModulosArbol] = useState([]);
  const [modulosExpandidos, setModulosExpandidos] = useState({});
  const [loadingPermisos, setLoadingPermisos] = useState(false);
  const [guardandoPermisos, setGuardandoPermisos] = useState(false);
  const [checks, setChecks] = useState({});

  // ─────────────────────────────────────────────────────────
  //  Cargar perfiles
  // ─────────────────────────────────────────────────────────
  const fetchPerfiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseURL}/api/Perfiles`);
      const lista = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setDatos(lista);
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error al cargar",
        text: "No se pudieron cargar los perfiles.",
        confirmButtonColor: "#1a56db",
      });
    } finally {
      setLoading(false);
    }
  }, [baseURL]);

  useEffect(() => { fetchPerfiles(); }, [fetchPerfiles]);

  // ── Filtro local ─────────────────────────────────────────
  useEffect(() => {
    const q = busqueda.toLowerCase();
    setDatosFiltrados(
      !q
        ? datos
        : datos.filter(
            (p) =>
              String(p.id_perfil).includes(q) ||
              p.nombre?.toLowerCase().includes(q)
          )
    );
  }, [datos, busqueda]);

  // ── Modal: abrir nuevo ───────────────────────────────────
  const abrirNuevo = () => {
    setEditando(false);
    setPerfilActivo(null);
    setNombrePerfil("");
    setModalVisible(true);
  };

  // ── Modal: abrir editar ──────────────────────────────────
  const abrirEditar = (perfil) => {
    setEditando(true);
    setPerfilActivo(perfil);
    setNombrePerfil(perfil.nombre || "");
    setModalVisible(true);
  };

  // ── Guardar perfil ───────────────────────────────────────
  const guardar = async () => {
    if (!nombrePerfil.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Ingresa el nombre del perfil.",
        confirmButtonColor: "#1a56db",
      });
      return;
    }

    setGuardando(true);
    try {
      const usu =
        cookies.get("nombreUsuario") || cookies.get("idUsuario") || "ADMIN";

      if (editando) {
        await axios.put(`${baseURL}/api/Perfiles/${perfilActivo.id_perfil}`, {
          nombre: nombrePerfil.trim(),
          usu_mod: usu,
        });
      } else {
        await axios.post(`${baseURL}/api/Perfiles`, {
          nombre: nombrePerfil.trim(),
          usu_crea: usu,
        });
      }

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: editando ? "Perfil actualizado" : "Perfil creado",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      });
      setModalVisible(false);
      fetchPerfiles();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.mensaje || "Error al guardar el perfil.",
        confirmButtonColor: "#1a56db",
      });
    } finally {
      setGuardando(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") guardar();
  };

  // ── Eliminar perfil ──────────────────────────────────────
  const eliminarPerfil = async (perfil) => {
    const confirm = await Swal.fire({
      title: "¿Eliminar perfil?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });
    if (!confirm.isConfirmed) return;

    setEliminando(perfil.id_perfil);
    try {
      await axios.delete(`${baseURL}/api/Perfiles/${perfil.id_perfil}`);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Perfil eliminado correctamente",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      });
      fetchPerfiles();
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data || {};

      if (status === 404) {
        Swal.fire({
          icon: "error",
          title: "El perfil no existe",
          text: data.mensaje || "El perfil ya fue eliminado.",
          confirmButtonColor: "#1a56db",
        });
      } else if (status === 409) {
        const usuarios = data.usuariosAsignados || [];
        const filas = usuarios
          .map(
            (u) =>
              `<tr><td>${u.nombreUsuario}</td><td>${u.nombresApellidos}</td></tr>`
          )
          .join("");

        Swal.fire({
          title: "No se puede eliminar",
          icon: "warning",
          html: `
            <p>${data.mensaje || "Hay usuarios asignados a este perfil."}</p>
            <table class="table table-sm table-bordered mt-2" style="font-size:0.85rem;">
              <thead>
                <tr><th>Usuario</th><th>Nombre</th></tr>
              </thead>
              <tbody>${filas}</tbody>
            </table>
          `,
          confirmButtonText: "Entendido",
          confirmButtonColor: "#1a56db",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: data.mensaje || "Error al eliminar el perfil.",
          confirmButtonColor: "#1a56db",
        });
      }
    } finally {
      setEliminando(null);
    }
  };

  // ── Abrir sección de permisos ────────────────────────────
  const abrirPermisos = async (perfil) => {
    setPermisosPerfil(perfil);
    setLoadingPermisos(true);
    setModulosArbol([]);
    setChecks({});

    try {
      const [resModulos, resPermisos] = await Promise.all([
        axios.get(`${baseURL}/api/Modulos`),
        axios.get(`${baseURL}/api/Perfiles/${perfil.id_perfil}/Permisos`),
      ]);

      const mods = Array.isArray(resModulos.data)
        ? resModulos.data
        : resModulos.data?.data || [];

      const perms = Array.isArray(resPermisos.data)
        ? resPermisos.data
        : resPermisos.data?.data || [];

      const arbol = agruparModulosPlano(mods);
      setModulosArbol(arbol);

      const expandidos = {};
      arbol.forEach((mod) => {
        if (mod.submodulos.length > 0) {
          expandidos[mod.id_modulo] = true;
        }
      });
      setModulosExpandidos(expandidos);

      const inicial = {};
      arbol.forEach((mod) => {
        const keyPadre = `${mod.id_modulo}_0`;
        inicial[keyPadre] = perms.some(
          (p) =>
            String(p.id_modulo) === String(mod.id_modulo) &&
            (p.id_submodulo === null || p.id_submodulo === 0) &&
            p.puede_ver === true
        );
        mod.submodulos.forEach((sub) => {
          const keySub = `${sub.id_modulo}_${sub.id_submodulo}`;
          inicial[keySub] = perms.some(
            (p) =>
              String(p.id_modulo) === String(sub.id_modulo) &&
              String(p.id_submodulo) === String(sub.id_submodulo) &&
              p.puede_ver === true
          );
        });
      });
      setChecks(inicial);
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error al cargar",
        text: "No se pudieron cargar los permisos.",
        confirmButtonColor: "#1a56db",
      });
    } finally {
      setLoadingPermisos(false);
    }
  };

  // ── Cerrar sección de permisos ───────────────────────────
  const cerrarPermisos = () => {
    setPermisosPerfil(null);
    setModulosArbol([]);
    setModulosExpandidos({});
    setChecks({});
  };

  // ── Toggle checkbox Acceso ───────────────────────────────
  const toggleAcceso = (idModulo, idSubmodulo) => {
    const key = `${idModulo}_${idSubmodulo || 0}`;
    setChecks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Guardar permisos ─────────────────────────────────────
  const guardarPermisos = async () => {
    const usu =
      cookies.get("nombreUsuario") || cookies.get("idUsuario") || "ADMIN";

    const permisos = [];
    modulosArbol.forEach((mod) => {
      const keyPadre = `${mod.id_modulo}_0`;
      const accesoPadre = checks[keyPadre] || false;
      permisos.push({
        id_modulo: mod.id_modulo,
        id_submodulo: null,
        puede_ver: accesoPadre,
        puede_crear: accesoPadre,
        puede_editar: accesoPadre,
        puede_eliminar: accesoPadre,
      });
      mod.submodulos.forEach((sub) => {
        const keySub = `${sub.id_modulo}_${sub.id_submodulo}`;
        const accesoSub = checks[keySub] || false;
        permisos.push({
          id_modulo: sub.id_modulo,
          id_submodulo: sub.id_submodulo,
          puede_ver: accesoSub,
          puede_crear: accesoSub,
          puede_editar: accesoSub,
          puede_eliminar: accesoSub,
        });
      });
    });

    setGuardandoPermisos(true);
    try {
      const res = await axios.post(
        `${baseURL}/api/Perfiles/${permisosPerfil.id_perfil}/Permisos`,
        { usu_crea: usu, permisos }
      );

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Permisos guardados correctamente",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      });
      cerrarPermisos();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.mensaje || "Error al guardar permisos.",
        confirmButtonColor: "#1a56db",
      });
    } finally {
      setGuardandoPermisos(false);
    }
  };

  // ── Toggle expandir/colapsar submódulos ──────────────────
  const toggleExpandir = (idModulo) => {
    setModulosExpandidos((prev) => ({
      ...prev,
      [idModulo]: !prev[idModulo],
    }));
  };

  // ─────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <div className="telefonos-container">
      {/* ── Sección Tabla de Perfiles ── */}
      <div className="telefonos-card">
        <div className="card-header-custom d-flex align-items-center justify-content-between">
          <h3 className="card-title-custom">
            <FaLock style={{ marginRight: 10 }} />
            Perfiles
          </h3>
          <button className="btn-agregar-custom" onClick={abrirNuevo}>
            <FaPlus /> Nuevo Perfil
          </button>
        </div>

        <div className="form-section">
          <div className="perfiles-filtros-wrap">
            <div className="usuarios-search-wrap" style={{ width: "100%", maxWidth: 400 }}>
              <FaSearch className="usuarios-search-icon" />
              <input
                type="text"
                className="custom-input usuarios-search-input"
                placeholder="Buscar por id o nombre..."
                autoComplete="off"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
            <div className="usuarios-contador">
              <span className="usuarios-badge-count">
                {datosFiltrados.length} perfil{datosFiltrados.length !== 1 ? "es" : ""}
              </span>
            </div>
          </div>

          <div className="table-responsive usuarios-table-wrap">
            <table className="usuarios-table perfiles-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nombre</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="3" className="sin-resultados">
                      <i className="fas fa-spinner fa-spin mr-2" /> Cargando...
                    </td>
                  </tr>
                ) : datosFiltrados.length > 0 ? (
                  datosFiltrados.map((p, idx) => (
                    <tr key={p.id_perfil}>
                      <td className="td-id">{idx + 1}</td>
                      <td>
                        <span className="usuario-username">{p.nombre}</span>
                      </td>
                      <td>
                        <div className="acciones-wrap">
                          <button
                            className="btn-accion-tabla btn-editar"
                            onClick={() => abrirEditar(p)}
                          >
                            <FaEdit /> Editar
                          </button>
                          <button
                            className="btn-accion-tabla btn-clave"
                            onClick={() => abrirPermisos(p)}
                          >
                            <FaLock /> Permisos
                          </button>
                          <button
                            className="btn-accion-tabla btn-eliminar-prosp"
                            onClick={() => eliminarPerfil(p)}
                            disabled={eliminando === p.id_perfil}
                          >
                            {eliminando === p.id_perfil ? (
                              <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} />
                            ) : (
                              <FaTrash />
                            )}{" "}
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="sin-resultados">
                      <i className="fas fa-search mr-2" /> No se encontraron perfiles
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Modal Permisos ── */}
      {permisosPerfil && (
        <div className="modal-overlay" onClick={cerrarPermisos}>
          <div
            className="modal-usuarios"
            style={{ maxWidth: "700px", maxHeight: "85vh", display: "flex", flexDirection: "column" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-custom">
              <h5 className="modal-title-custom">
                <FaLock style={{ marginRight: 8 }} />
                Permisos de: {permisosPerfil.nombre}
              </h5>
              <button className="modal-close" onClick={cerrarPermisos}>
                <FaTimes size={14} />
              </button>
            </div>
            <div className="modal-body-custom" style={{ overflowY: "auto", flex: 1 }}>
              {loadingPermisos ? (
                <div className="sin-resultados" style={{ padding: "2rem" }}>
                  <i className="fas fa-spinner fa-spin mr-2" /> Cargando módulos y permisos...
                </div>
              ) : (
                <div className="table-responsive usuarios-table-wrap">
                  <table className="usuarios-table permisos-table">
                    <thead>
                      <tr>
                        <th>Módulo</th>
                        <th className="permisos-th-center">Acceso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modulosArbol.length > 0 ? (
                        modulosArbol.map((mod) => {
                          const tieneSubs = mod.submodulos.length > 0;
                          const expandido = modulosExpandidos[mod.id_modulo] !== false;
                          return (
                          <React.Fragment key={`mod_${mod.id_modulo}`}>
                            <tr
                              className={`permisos-fila ${tieneSubs ? "permisos-fila-padre" : ""}`}
                              onClick={tieneSubs ? () => toggleExpandir(mod.id_modulo) : undefined}
                            >
                              <td className="permisos-nombre">
                                {tieneSubs && (
                                  <span className="permisos-toggle">
                                    {expandido ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                                  </span>
                                )}
                                {mod.nombre}
                              </td>
                              <td className="permisos-check" onClick={(e) => e.stopPropagation()}>
                                <label className="permisos-checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={checks[`${mod.id_modulo}_0`] || false}
                                    onChange={() => toggleAcceso(mod.id_modulo, null)}
                                  />
                                  <span className="permisos-check-text">Acceso</span>
                                </label>
                              </td>
                            </tr>
                            {mod.submodulos.map((sub) => (
                              <tr
                                key={`sub_${sub.id_modulo}_${sub.id_submodulo}`}
                                className={`permisos-fila permisos-submodulo ${!expandido ? "permisos-submodulo-colapsado" : ""}`}
                              >
                                <td
                                  className="permisos-nombre"
                                  style={{ paddingLeft: "44px" }}
                                >
                                  <span className="permisos-sub-indicador">└</span>
                                  {sub.nombre}
                                </td>
                                <td className="permisos-check">
                                  <label className="permisos-checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={
                                        checks[`${sub.id_modulo}_${sub.id_submodulo}`] || false
                                      }
                                      onChange={() =>
                                        toggleAcceso(sub.id_modulo, sub.id_submodulo)
                                      }
                                    />
                                    <span className="permisos-check-text">Acceso</span>
                                  </label>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        )})
                      ) : (
                        <tr>
                          <td colSpan="2" className="sin-resultados">
                            No hay módulos disponibles.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer-custom">
              <button className="btn-modal-cancelar" onClick={cerrarPermisos}>
                Cerrar
              </button>
              <button
                className="btn-save-custom text-white"
                onClick={guardarPermisos}
                disabled={guardandoPermisos || loadingPermisos}
              >
                {guardandoPermisos ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-1" /> Guardando...
                  </>
                ) : (
                  <>
                    <FaSave style={{ marginRight: 6 }} /> Guardar Permisos
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Nuevo / Editar Perfil ── */}
      {modalVisible && (
        <div className="modal-overlay" onClick={() => setModalVisible(false)}>
          <div className="modal-usuarios" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <h5 className="modal-title-custom">
                {editando ? "✏️ Editar Perfil" : "➕ Nuevo Perfil"}
              </h5>
              <button className="modal-close" onClick={() => setModalVisible(false)}>
                <FaTimes size={14} />
              </button>
            </div>
            <div className="modal-body-custom">
              <div className="custom-form-group">
                <label className="custom-label">
                  Nombre <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="custom-input"
                  placeholder="Nombre del perfil"
                  autoComplete="off"
                  maxLength={100}
                  value={nombrePerfil}
                  onChange={(e) => setNombrePerfil(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>
            <div className="modal-footer-custom">
              <button className="btn-modal-cancelar" onClick={() => setModalVisible(false)}>
                Cancelar
              </button>
              <button className="btn-save-custom text-white" onClick={guardar} disabled={guardando}>
                {guardando ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-1" /> Guardando...
                  </>
                ) : (
                  "Guardar"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Perfiles;
